import React, { useState, useEffect, useRef } from "react";
import LoadingAnimation from "../LoadingAnimation";
import { toast } from "react-toastify";

import { db } from "../../firebase";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";

// ─────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────

const DEFAULT_CONTRIBUTOR_GROUPS = [
  "VISIONARY LEADERS",
  "STRATEGIC MINDS",
  "THE ARCHITECTS",
  "INNOVATION COMMANDERS",
  "THE POWER CIRCLE",
  "THE BRAINTRUST",
  "RISING TALENT",
  "YOUNG ACHIEVERS",
];

const DEFAULT_ROLES_BY_GROUP = {
  "VISIONARY LEADERS": ["CEO & Owner", "Chief Technology Officer"],
  "STRATEGIC MINDS": ["Human Resource Manager"],
  "THE ARCHITECTS": ["Senior Graphic Producer"],
  "INNOVATION COMMANDERS": ["Multimedia Manager", "UI/UX Engineer"],
  "THE POWER CIRCLE": ["Software Engineer"],
  "THE BRAINTRUST": ["Multimedia Artist"],
  "RISING TALENT": ["Junior Graphic Designer"],
  "YOUNG ACHIEVERS": ["Junior Graphic Designer"],
};

const DEFAULT_DESIGNATIONS = [
  "Senior Graphic Producer",
  "Disciplinary Committee Member",
  "Head of Department",
];

const EMPTY_FORM = { group: "", role: "", designation: "", name: "", image: "" };

// ─── CLOUDINARY UPLOAD FUNCTION ────────────────────────────────────────────
const uploadImage = async (file) => {
  const url = "https://api.cloudinary.com/v1_1/djgfcak5i/image/upload";
  const data = new FormData();
  data.append("file", file);
  data.append("upload_preset", "front_upload");
  data.append("folder", "contributors");
  
  const res = await fetch(url, { method: "POST", body: data });
  const json = await res.json();
  return json.secure_url;
};

// ─────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────

const Managecontributors = () => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [contributors, setContributors] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [deletePopup, setDeletePopup] = useState({ visible: false, id: null });
  const [isDragOver, setIsDragOver] = useState(false);
  
  // Dynamic data management
  const [contributorGroups, setContributorGroups] = useState(DEFAULT_CONTRIBUTOR_GROUPS);
  const [rolesByGroup, setRolesByGroup] = useState(DEFAULT_ROLES_BY_GROUP);
  const [designations, setDesignations] = useState(DEFAULT_DESIGNATIONS);
  
  // Add modals
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showDesignationModal, setShowDesignationModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newRoleName, setNewRoleName] = useState("");
  const [selectedGroupForRole, setSelectedGroupForRole] = useState("");
  const [newDesignationName, setNewDesignationName] = useState("");

  // Manage modals
  const [showManageGroupsModal, setShowManageGroupsModal] = useState(false);
  const [showManageRolesModal, setShowManageRolesModal] = useState(false);
  const [showManageDesignationsModal, setShowManageDesignationsModal] = useState(false);

  // Inline editing state
  const [editingGroupIndex, setEditingGroupIndex] = useState(null);
  const [editingGroupValue, setEditingGroupValue] = useState("");
  const [manageRolesSelectedGroup, setManageRolesSelectedGroup] = useState("");
  const [editingRoleIndex, setEditingRoleIndex] = useState(null);
  const [editingRoleValue, setEditingRoleValue] = useState("");
  const [editingDesignationIndex, setEditingDesignationIndex] = useState(null);
  const [editingDesignationValue, setEditingDesignationValue] = useState("");

  // Delete confirmations inside manage modals
  const [deleteGroupConfirm, setDeleteGroupConfirm] = useState(null);
  const [deleteRoleConfirm, setDeleteRoleConfirm] = useState(null);
  const [deleteDesignationConfirm, setDeleteDesignationConfirm] = useState(null);

  const formTopRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageDropRef = useRef(null);
  
  // Refs for modal overlay click handling
  const manageGroupsOverlayRef = useRef(null);
  const manageRolesOverlayRef = useRef(null);
  const manageDesignationsOverlayRef = useRef(null);
  const groupModalOverlayRef = useRef(null);
  const roleModalOverlayRef = useRef(null);
  const designationModalOverlayRef = useRef(null);
  const deletePopupOverlayRef = useRef(null);

  useEffect(() => {
    fetchContributors();
    loadCustomData();
  }, []);

  const loadCustomData = () => {
    const savedGroups = localStorage.getItem("contributorGroups");
    if (savedGroups) setContributorGroups(JSON.parse(savedGroups));
    const savedRoles = localStorage.getItem("rolesByGroup");
    if (savedRoles) setRolesByGroup(JSON.parse(savedRoles));
    const savedDesignations = localStorage.getItem("designations");
    if (savedDesignations) setDesignations(JSON.parse(savedDesignations));
  };

  const saveCustomData = (groups, roles, desigs) => {
    localStorage.setItem("contributorGroups", JSON.stringify(groups));
    localStorage.setItem("rolesByGroup", JSON.stringify(roles));
    localStorage.setItem("designations", JSON.stringify(desigs));
  };

  const fetchContributors = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, "contributors"));
      const list = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort(
        (a, b) =>
          contributorGroups.indexOf(a.group) - contributorGroups.indexOf(b.group)
      );
      setContributors(list);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch contributors.");
    } finally {
      setLoading(false);
    }
  };

  // ── DRAG AND DROP IMAGE HANDLERS ─────────────────────────
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error("Please drop an image file.");
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB.");
        return;
      }

      // Process the dropped file
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target.result);
      reader.readAsDataURL(file);
      
      setSelectedFile(file);
      
      // Also update the file input ref for consistency
      if (fileInputRef.current) {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        fileInputRef.current.files = dataTransfer.files;
      }
      
      toast.success("Image dropped successfully!");
    }
  };
  // ─────────────────────────────────────────────────────────

  // ── ADD HANDLERS ──────────────────────────────────────

  const handleAddGroup = () => {
    if (!newGroupName.trim()) { toast.error("Please enter a group name"); return; }
    const upperCaseName = newGroupName.toUpperCase().trim();
    if (contributorGroups.includes(upperCaseName)) { toast.error("Group already exists!"); return; }
    const updatedGroups = [...contributorGroups, upperCaseName];
    const updatedRoles = { ...rolesByGroup, [upperCaseName]: [] };
    setContributorGroups(updatedGroups);
    setRolesByGroup(updatedRoles);
    saveCustomData(updatedGroups, updatedRoles, designations);
    toast.success(`Group "${upperCaseName}" added successfully!`);
    setNewGroupName("");
    setShowGroupModal(false);
  };

  const handleAddRole = () => {
    if (!newRoleName.trim()) { toast.error("Please enter a role name"); return; }
    if (!selectedGroupForRole) { toast.error("Please select a group for this role"); return; }
    const roleName = newRoleName.trim();
    const currentRoles = rolesByGroup[selectedGroupForRole] || [];
    if (currentRoles.includes(roleName)) { toast.error("Role already exists in this group!"); return; }
    const updatedRoles = { ...rolesByGroup, [selectedGroupForRole]: [...currentRoles, roleName] };
    setRolesByGroup(updatedRoles);
    saveCustomData(contributorGroups, updatedRoles, designations);
    toast.success(`Role "${roleName}" added to ${selectedGroupForRole}!`);
    setNewRoleName("");
    setSelectedGroupForRole("");
    setShowRoleModal(false);
  };

  const handleAddDesignation = () => {
    if (!newDesignationName.trim()) { toast.error("Please enter a designation"); return; }
    const designationName = newDesignationName.trim();
    if (designations.includes(designationName)) { toast.error("Designation already exists!"); return; }
    const updatedDesignations = [...designations, designationName];
    setDesignations(updatedDesignations);
    saveCustomData(contributorGroups, rolesByGroup, updatedDesignations);
    toast.success(`Designation "${designationName}" added successfully!`);
    setNewDesignationName("");
    setShowDesignationModal(false);
  };

  // ── MANAGE GROUPS CRUD ────────────────────────────────

  const handleEditGroupSave = (index) => {
    const trimmed = editingGroupValue.toUpperCase().trim();
    if (!trimmed) { toast.error("Group name cannot be empty"); return; }
    const oldName = contributorGroups[index];
    if (trimmed === oldName) { setEditingGroupIndex(null); return; }
    if (contributorGroups.includes(trimmed)) { toast.error("Group already exists!"); return; }
    const updatedGroups = [...contributorGroups];
    updatedGroups[index] = trimmed;
    const updatedRoles = { ...rolesByGroup };
    if (updatedRoles[oldName]) {
      updatedRoles[trimmed] = updatedRoles[oldName];
      delete updatedRoles[oldName];
    }
    setContributorGroups(updatedGroups);
    setRolesByGroup(updatedRoles);
    saveCustomData(updatedGroups, updatedRoles, designations);
    toast.success("Group updated!");
    setEditingGroupIndex(null);
    setEditingGroupValue("");
  };

  const handleDeleteGroup = (index) => {
    const name = contributorGroups[index];
    const updatedGroups = contributorGroups.filter((_, i) => i !== index);
    const updatedRoles = { ...rolesByGroup };
    delete updatedRoles[name];
    setContributorGroups(updatedGroups);
    setRolesByGroup(updatedRoles);
    saveCustomData(updatedGroups, updatedRoles, designations);
    toast.success(`Group "${name}" deleted!`);
    setDeleteGroupConfirm(null);
  };

  // ── MANAGE ROLES CRUD ─────────────────────────────────

  const handleEditRoleSave = (group, index) => {
    const trimmed = editingRoleValue.trim();
    if (!trimmed) { toast.error("Role name cannot be empty"); return; }
    const currentRoles = rolesByGroup[group] || [];
    if (trimmed === currentRoles[index]) { setEditingRoleIndex(null); return; }
    if (currentRoles.includes(trimmed)) { toast.error("Role already exists!"); return; }
    const updatedRoles = currentRoles.map((r, i) => (i === index ? trimmed : r));
    const newRolesByGroup = { ...rolesByGroup, [group]: updatedRoles };
    setRolesByGroup(newRolesByGroup);
    saveCustomData(contributorGroups, newRolesByGroup, designations);
    toast.success("Role updated!");
    setEditingRoleIndex(null);
    setEditingRoleValue("");
  };

  const handleDeleteRole = (group, index) => {
    const updatedRoles = (rolesByGroup[group] || []).filter((_, i) => i !== index);
    const newRolesByGroup = { ...rolesByGroup, [group]: updatedRoles };
    setRolesByGroup(newRolesByGroup);
    saveCustomData(contributorGroups, newRolesByGroup, designations);
    toast.success("Role deleted!");
    setDeleteRoleConfirm(null);
  };

  // ── MANAGE DESIGNATIONS CRUD ──────────────────────────

  const handleEditDesignationSave = (index) => {
    const trimmed = editingDesignationValue.trim();
    if (!trimmed) { toast.error("Designation cannot be empty"); return; }
    if (trimmed === designations[index]) { setEditingDesignationIndex(null); return; }
    if (designations.includes(trimmed)) { toast.error("Designation already exists!"); return; }
    const updated = designations.map((d, i) => (i === index ? trimmed : d));
    setDesignations(updated);
    saveCustomData(contributorGroups, rolesByGroup, updated);
    toast.success("Designation updated!");
    setEditingDesignationIndex(null);
    setEditingDesignationValue("");
  };

  const handleDeleteDesignation = (index) => {
    const updated = designations.filter((_, i) => i !== index);
    setDesignations(updated);
    saveCustomData(contributorGroups, rolesByGroup, updated);
    toast.success("Designation deleted!");
    setDeleteDesignationConfirm(null);
  };

  // ── FORM HANDLERS ─────────────────────────────────────

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "group" ? { role: "" } : {}),
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error("Please upload an image file."); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image size should be less than 5MB."); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
    setSelectedFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.group || !formData.role || !formData.name) {
      toast.error("Please fill Group, Role, and Name."); return;
    }
    setLoading(true);
    let imageUrl = formData.image;
    if (selectedFile) {
      setUploading(true);
      try {
        imageUrl = await uploadImage(selectedFile);
      } catch (err) {
        console.error(err);
        toast.error("Image upload failed.");
        setLoading(false); setUploading(false); return;
      } finally { setUploading(false); }
    }
    try {
      const contributorData = { ...formData, image: imageUrl };
      if (editingId) {
        await updateDoc(doc(db, "contributors", editingId), contributorData);
        toast.success("Contributor updated!"); setEditingId(null);
      } else {
        await addDoc(collection(db, "contributors"), { ...contributorData, createdAt: serverTimestamp() });
        toast.success("Contributor added!");
      }
      setFormData(EMPTY_FORM); setImagePreview(""); setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchContributors();
    } catch (err) {
      console.error(err); toast.error("Error saving contributor.");
    } finally { setLoading(false); }
  };

  const handleEdit = (contributor) => {
    setFormData({
      group: contributor.group || "", role: contributor.role || "",
      designation: contributor.designation || "", name: contributor.name || "", image: contributor.image || "",
    });
    setImagePreview(contributor.image || ""); setSelectedFile(null); setEditingId(contributor.id);
    formTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleDelete = (id) => setDeletePopup({ visible: true, id });

  const confirmDelete = async () => {
    if (!deletePopup.id) return;
    setLoading(true);
    try {
      await deleteDoc(doc(db, "contributors", deletePopup.id));
      toast.success("Contributor deleted!"); fetchContributors();
    } catch (err) { console.error(err); toast.error("Error deleting contributor."); }
    finally { setLoading(false); setDeletePopup({ visible: false, id: null }); }
  };

  const cancelDelete = () => setDeletePopup({ visible: false, id: null });

  const availableRoles = formData.group ? rolesByGroup[formData.group] || [] : [];

  // Close modals when clicking outside
  const handleOverlayClick = (e, modalRef, closeFunction) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      closeFunction();
    }
  };

  // ── STYLES ────────────────────────────────────────────

  const modalStyles = {
    overlay: {
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, backdropFilter: "blur(4px)",
    },
    container: {
      background: "white", padding: "32px", borderRadius: "16px",
      width: "420px", maxWidth: "90%", boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
    },
    wideContainer: {
      background: "white", padding: "32px", borderRadius: "16px",
      width: "560px", maxWidth: "95%", maxHeight: "80vh", overflowY: "auto",
      boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
    },
    input: { width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "14px", marginBottom: "20px", boxSizing: "border-box" },
    select: { width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "14px", marginBottom: "20px", boxSizing: "border-box" },
    buttonPrimary: { background: "linear-gradient(135deg, #FF8000 0%, #FF6B00 100%)", color: "white", padding: "10px 20px", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "600", marginRight: "12px", fontSize: "13px" },
    buttonSecondary: { backgroundColor: "transparent", color: "#718096", padding: "10px 20px", border: "1px solid #e2e8f0", borderRadius: "8px", cursor: "pointer", fontSize: "13px" },
    iconBtn: (color = "#FF8000") => ({ background: "none", border: "none", cursor: "pointer", color, padding: "4px 6px", borderRadius: "4px", display: "inline-flex", alignItems: "center" }),
    listItem: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", borderRadius: "8px", marginBottom: "6px", background: "#f9fafb", border: "1px solid #e2e8f0", gap: "8px" },
    editInput: { flex: 1, padding: "6px 10px", borderRadius: "6px", border: "1px solid #FF8000", fontSize: "14px", outline: "none" },
  };

  const PlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  );

  const EditIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 512 512" fill="currentColor">
      <path d="M441 58.9L453.1 71c9.4 9.4 9.4 24.6 0 33.9L424 134.1L377.9 88L407 58.9c9.4-9.4 24.6-9.4 33.9 0zM209.8 256.2L344 121.9l46.1 46.1l-134.3 134.2c-2.9 2.9-6.5 5-10.4 6.1L186.9 325l16.7-58.5c1.1-3.9 3.2-7.5 6.1-10.4zM373.1 25L175.8 222.2c-8.7 8.7-15 19.4-18.3 31.1l-28.6 100c-2.4 8.4-.1 17.4 6.1 23.6s15.2 8.5 23.6 6.1l100-28.6c11.8-3.4 22.5-9.7 31.1-18.3L487 138.9c28.1-28.1 28.1-73.7 0-101.8L474.9 25c-28.1-28.1-73.7-28.1-101.8 0M88 64c-48.6 0-88 39.4-88 88v272c0 48.6 39.4 88 88 88h272c48.6 0 88-39.4 88-88V312c0-13.3-10.7-24-24-24s-24 10.7-24 24v112c0 22.1-17.9 40-40 40H88c-22.1 0-40-17.9-40-40V152c0-22.1 17.9-40 40-40h112c13.3 0 24-10.7 24-24s-10.7-24-24-24z" />
    </svg>
  );

  const TrashIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6zm2.46-7.12l1.41-1.41L12 12.59l2.12-2.12l1.41 1.41L13.41 14l2.12 2.12l-1.41 1.41L12 15.41l-2.12 2.12l-1.41-1.41L10.59 14zM15.5 4l-1-1h-5l-1 1H5v2h14V4z" />
    </svg>
  );

  const CheckIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
  );

  const XIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ cursor: "pointer" }}>
      <line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  );

  const GearIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"></circle>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
    </svg>
  );

  return (
    <div className="postjob-container" ref={formTopRef}>

      {/* ── Delete Contributor Popup ── */}
      {deletePopup.visible && (
        <div 
          style={modalStyles.overlay} 
          ref={deletePopupOverlayRef}
          onClick={(e) => handleOverlayClick(e, deletePopupOverlayRef, cancelDelete)}
        >
          <div style={modalStyles.container} onClick={(e) => e.stopPropagation()}>
            <div style={{ width: "52px", height: "52px", background: "linear-gradient(135deg, #f56565 0%, #ed8936 100%)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <span style={{ color: "white", fontSize: "20px", fontWeight: "bold" }}>!</span>
            </div>
            <h3 style={{ margin: "0 0 12px 0", color: "#2D3748", fontSize: "18px" }}>Confirm Delete</h3>
            <p style={{ margin: "0 0 28px 0", color: "#718096", fontSize: "14px", lineHeight: "1.4" }}>Are you sure you want to delete this contributor?</p>
            <div style={{ display: "flex", gap: "12px" }}>
              <button style={modalStyles.buttonSecondary} onClick={cancelDelete}>Cancel</button>
              <button style={modalStyles.buttonPrimary} onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Group Modal ── */}
      {showGroupModal && (
        <div 
          style={modalStyles.overlay}
          ref={groupModalOverlayRef}
          onClick={(e) => handleOverlayClick(e, groupModalOverlayRef, () => setShowGroupModal(false))}
        >
          <div style={modalStyles.container} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h3 style={{ margin: 0, color: "#2D3748" }}>Add New Group</h3>
              <button style={modalStyles.iconBtn("#718096")} onClick={() => setShowGroupModal(false)}>
                <XIcon />
              </button>
            </div>
            <input type="text" placeholder="Enter group name (e.g., EXECUTIVE BOARD)" value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} style={modalStyles.input} autoFocus onKeyDown={(e) => e.key === "Enter" && handleAddGroup()} />
            <div style={{ display: "flex", gap: "12px" }}>
              <button style={modalStyles.buttonPrimary} onClick={handleAddGroup}>Add Group</button>
              <button style={modalStyles.buttonSecondary} onClick={() => setShowGroupModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Role Modal ── */}
      {showRoleModal && (
        <div 
          style={modalStyles.overlay}
          ref={roleModalOverlayRef}
          onClick={(e) => handleOverlayClick(e, roleModalOverlayRef, () => setShowRoleModal(false))}
        >
          <div style={modalStyles.container} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h3 style={{ margin: 0, color: "#2D3748" }}>Add New Role</h3>
              <button style={modalStyles.iconBtn("#718096")} onClick={() => setShowRoleModal(false)}>
                <XIcon />
              </button>
            </div>
            <select value={selectedGroupForRole} onChange={(e) => setSelectedGroupForRole(e.target.value)} style={modalStyles.select}>
              <option value="">Select Group/Category</option>
              {contributorGroups.map((group) => <option key={group} value={group}>{group}</option>)}
            </select>
            <input type="text" placeholder="Enter role name (e.g., Project Manager)" value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)} style={modalStyles.input} onKeyDown={(e) => e.key === "Enter" && handleAddRole()} />
            <div style={{ display: "flex", gap: "12px" }}>
              <button style={modalStyles.buttonPrimary} onClick={handleAddRole}>Add Role</button>
              <button style={modalStyles.buttonSecondary} onClick={() => setShowRoleModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Designation Modal ── */}
      {showDesignationModal && (
        <div 
          style={modalStyles.overlay}
          ref={designationModalOverlayRef}
          onClick={(e) => handleOverlayClick(e, designationModalOverlayRef, () => setShowDesignationModal(false))}
        >
          <div style={modalStyles.container} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h3 style={{ margin: 0, color: "#2D3748" }}>Add New Designation</h3>
              <button style={modalStyles.iconBtn("#718096")} onClick={() => setShowDesignationModal(false)}>
                <XIcon />
              </button>
            </div>
            <input type="text" placeholder="Enter designation (e.g., Technical Lead)" value={newDesignationName} onChange={(e) => setNewDesignationName(e.target.value)} style={modalStyles.input} autoFocus onKeyDown={(e) => e.key === "Enter" && handleAddDesignation()} />
            <div style={{ display: "flex", gap: "12px" }}>
              <button style={modalStyles.buttonPrimary} onClick={handleAddDesignation}>Add Designation</button>
              <button style={modalStyles.buttonSecondary} onClick={() => setShowDesignationModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
          MANAGE GROUPS MODAL
      ══════════════════════════════════════════════════ */}
      {showManageGroupsModal && (
        <div 
          style={modalStyles.overlay}
          ref={manageGroupsOverlayRef}
          onClick={(e) => handleOverlayClick(e, manageGroupsOverlayRef, () => { setShowManageGroupsModal(false); setEditingGroupIndex(null); setDeleteGroupConfirm(null); })}
        >
          <div style={modalStyles.wideContainer} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h3 style={{ margin: 0, color: "#2D3748" }}>Manage Groups / Categories</h3>
              <button style={modalStyles.iconBtn("#718096")} onClick={() => { setShowManageGroupsModal(false); setEditingGroupIndex(null); setDeleteGroupConfirm(null); }}>
                <XIcon />
              </button>
            </div>

            {contributorGroups.length === 0 && <p style={{ color: "#a0aec0", textAlign: "center", padding: "20px 0" }}>No groups added yet.</p>}

            {contributorGroups.map((group, index) => (
              <div key={index}>
                {deleteGroupConfirm === index ? (
                  <div style={{ ...modalStyles.listItem, background: "#fff5f5", borderColor: "#feb2b2" }}>
                    <span style={{ flex: 1, fontSize: "14px", color: "#c53030" }}>Delete "<strong>{group}</strong>" and all its roles?</span>
                    <div style={{ display: "flex", gap: "6px" }}>
                      <button style={{ ...modalStyles.iconBtn("#e53e3e") }} title="Confirm Delete" onClick={() => handleDeleteGroup(index)}><CheckIcon /></button>
                      <button style={{ ...modalStyles.iconBtn("#718096") }} title="Cancel" onClick={() => setDeleteGroupConfirm(null)}><XIcon /></button>
                    </div>
                  </div>
                ) : editingGroupIndex === index ? (
                  <div style={modalStyles.listItem}>
                    <input
                      autoFocus
                      value={editingGroupValue}
                      onChange={(e) => setEditingGroupValue(e.target.value)}
                      style={modalStyles.editInput}
                      onKeyDown={(e) => { if (e.key === "Enter") handleEditGroupSave(index); if (e.key === "Escape") setEditingGroupIndex(null); }}
                    />
                    <div style={{ display: "flex", gap: "6px" }}>
                      <button style={modalStyles.iconBtn("#38a169")} title="Save" onClick={() => handleEditGroupSave(index)}><CheckIcon /></button>
                      <button style={modalStyles.iconBtn("#718096")} title="Cancel" onClick={() => setEditingGroupIndex(null)}><XIcon /></button>
                    </div>
                  </div>
                ) : (
                  <div style={modalStyles.listItem}>
                    <span style={{ flex: 1, fontSize: "14px", fontWeight: "500", color: "#2D3748" }}>{group}</span>
                    <span style={{ fontSize: "12px", color: "#a0aec0", marginRight: "8px" }}>{(rolesByGroup[group] || []).length} roles</span>
                    <div style={{ display: "flex", gap: "4px" }}>
                      <button style={modalStyles.iconBtn("#FF8000")} title="Edit" onClick={() => { setEditingGroupIndex(index); setEditingGroupValue(group); }}><EditIcon /></button>
                      <button style={modalStyles.iconBtn("#e53e3e")} title="Delete" onClick={() => setDeleteGroupConfirm(index)}><TrashIcon /></button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end" }}>
              <button style={modalStyles.buttonPrimary} onClick={() => { setShowManageGroupsModal(false); setShowGroupModal(true); }}>
                + Add New Group
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
          MANAGE ROLES MODAL - Same list view as Groups
      ══════════════════════════════════════════════════ */}
      {showManageRolesModal && (
        <div 
          style={modalStyles.overlay}
          ref={manageRolesOverlayRef}
          onClick={(e) => handleOverlayClick(e, manageRolesOverlayRef, () => { setShowManageRolesModal(false); setEditingRoleIndex(null); setDeleteRoleConfirm(null); })}
        >
          <div style={modalStyles.wideContainer} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h3 style={{ margin: 0, color: "#2D3748" }}>Manage Roles</h3>
              <button style={modalStyles.iconBtn("#718096")} onClick={() => { setShowManageRolesModal(false); setEditingRoleIndex(null); setDeleteRoleConfirm(null); }}>
                <XIcon />
              </button>
            </div>

            {/* Group selector */}
            <select
              value={manageRolesSelectedGroup}
              onChange={(e) => { setManageRolesSelectedGroup(e.target.value); setEditingRoleIndex(null); setDeleteRoleConfirm(null); }}
              style={{ ...modalStyles.select, marginBottom: "16px" }}
            >
              <option value="">Select a Group to manage its roles</option>
              {contributorGroups.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>

            {manageRolesSelectedGroup && (
              <>
                {(rolesByGroup[manageRolesSelectedGroup] || []).length === 0 && (
                  <p style={{ color: "#a0aec0", textAlign: "center", padding: "16px 0", fontSize: "14px" }}>No roles in this group yet.</p>
                )}

                {(rolesByGroup[manageRolesSelectedGroup] || []).map((role, index) => (
                  <div key={index}>
                    {deleteRoleConfirm === index ? (
                      <div style={{ ...modalStyles.listItem, background: "#fff5f5", borderColor: "#feb2b2" }}>
                        <span style={{ flex: 1, fontSize: "14px", color: "#c53030" }}>Delete role "<strong>{role}</strong>"?</span>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button style={modalStyles.iconBtn("#e53e3e")} title="Confirm" onClick={() => handleDeleteRole(manageRolesSelectedGroup, index)}><CheckIcon /></button>
                          <button style={modalStyles.iconBtn("#718096")} title="Cancel" onClick={() => setDeleteRoleConfirm(null)}><XIcon /></button>
                        </div>
                      </div>
                    ) : editingRoleIndex === index ? (
                      <div style={modalStyles.listItem}>
                        <input
                          autoFocus
                          value={editingRoleValue}
                          onChange={(e) => setEditingRoleValue(e.target.value)}
                          style={modalStyles.editInput}
                          onKeyDown={(e) => { if (e.key === "Enter") handleEditRoleSave(manageRolesSelectedGroup, index); if (e.key === "Escape") setEditingRoleIndex(null); }}
                        />
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button style={modalStyles.iconBtn("#38a169")} title="Save" onClick={() => handleEditRoleSave(manageRolesSelectedGroup, index)}><CheckIcon /></button>
                          <button style={modalStyles.iconBtn("#718096")} title="Cancel" onClick={() => setEditingRoleIndex(null)}><XIcon /></button>
                        </div>
                      </div>
                    ) : (
                      <div style={modalStyles.listItem}>
                        <span style={{ flex: 1, fontSize: "14px", color: "#2D3748" }}>{role}</span>
                        <div style={{ display: "flex", gap: "4px" }}>
                          <button style={modalStyles.iconBtn("#FF8000")} title="Edit" onClick={() => { setEditingRoleIndex(index); setEditingRoleValue(role); }}><EditIcon /></button>
                          <button style={modalStyles.iconBtn("#e53e3e")} title="Delete" onClick={() => setDeleteRoleConfirm(index)}><TrashIcon /></button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end" }}>
                  <button style={modalStyles.buttonPrimary} onClick={() => { setShowManageRolesModal(false); setSelectedGroupForRole(manageRolesSelectedGroup); setShowRoleModal(true); }}>
                    + Add New Role
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
          MANAGE DESIGNATIONS MODAL
      ══════════════════════════════════════════════════ */}
      {showManageDesignationsModal && (
        <div 
          style={modalStyles.overlay}
          ref={manageDesignationsOverlayRef}
          onClick={(e) => handleOverlayClick(e, manageDesignationsOverlayRef, () => { setShowManageDesignationsModal(false); setEditingDesignationIndex(null); setDeleteDesignationConfirm(null); })}
        >
          <div style={modalStyles.wideContainer} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h3 style={{ margin: 0, color: "#2D3748" }}>Manage Designations</h3>
              <button style={modalStyles.iconBtn("#718096")} onClick={() => { setShowManageDesignationsModal(false); setEditingDesignationIndex(null); setDeleteDesignationConfirm(null); }}>
                <XIcon />
              </button>
            </div>

            {designations.length === 0 && <p style={{ color: "#a0aec0", textAlign: "center", padding: "20px 0" }}>No designations added yet.</p>}

            {designations.map((desig, index) => (
              <div key={index}>
                {deleteDesignationConfirm === index ? (
                  <div style={{ ...modalStyles.listItem, background: "#fff5f5", borderColor: "#feb2b2" }}>
                    <span style={{ flex: 1, fontSize: "14px", color: "#c53030" }}>Delete "<strong>{desig}</strong>"?</span>
                    <div style={{ display: "flex", gap: "6px" }}>
                      <button style={modalStyles.iconBtn("#e53e3e")} title="Confirm" onClick={() => handleDeleteDesignation(index)}><CheckIcon /></button>
                      <button style={modalStyles.iconBtn("#718096")} title="Cancel" onClick={() => setDeleteDesignationConfirm(null)}><XIcon /></button>
                    </div>
                  </div>
                ) : editingDesignationIndex === index ? (
                  <div style={modalStyles.listItem}>
                    <input
                      autoFocus
                      value={editingDesignationValue}
                      onChange={(e) => setEditingDesignationValue(e.target.value)}
                      style={modalStyles.editInput}
                      onKeyDown={(e) => { if (e.key === "Enter") handleEditDesignationSave(index); if (e.key === "Escape") setEditingDesignationIndex(null); }}
                    />
                    <div style={{ display: "flex", gap: "6px" }}>
                      <button style={modalStyles.iconBtn("#38a169")} title="Save" onClick={() => handleEditDesignationSave(index)}><CheckIcon /></button>
                      <button style={modalStyles.iconBtn("#718096")} title="Cancel" onClick={() => setEditingDesignationIndex(null)}><XIcon /></button>
                    </div>
                  </div>
                ) : (
                  <div style={modalStyles.listItem}>
                    <span style={{ flex: 1, fontSize: "14px", color: "#2D3748" }}>{desig}</span>
                    <div style={{ display: "flex", gap: "4px" }}>
                      <button style={modalStyles.iconBtn("#FF8000")} title="Edit" onClick={() => { setEditingDesignationIndex(index); setEditingDesignationValue(desig); }}><EditIcon /></button>
                      <button style={modalStyles.iconBtn("#e53e3e")} title="Delete" onClick={() => setDeleteDesignationConfirm(index)}><TrashIcon /></button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end" }}>
              <button style={modalStyles.buttonPrimary} onClick={() => { setShowManageDesignationsModal(false); setShowDesignationModal(true); }}>
                + Add New Designation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Page Heading ── */}
      <h2 className="page-heading">
        <i className="fas fa-users" style={{ color: "#FF8000" }}></i>
        {editingId ? " Edit Contributor" : " Add New Contributor"}
      </h2>

      {/* ── Form ── */}
      <form onSubmit={handleSubmit} className="postjob-form">

        {/* 1 — Group / Category with Add + Manage Buttons */}
        <div style={{ marginBottom: "16px" }}>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <select name="group" value={formData.group} onChange={handleChange} required style={{ flex: 1, padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
              <option value="">Select Category / Group</option>
              {contributorGroups.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            <button type="button" onClick={() => setShowGroupModal(true)} title="Add Group"
              style={{ width: "fit-content", padding: "12px", backgroundColor: "#fff", border: "1px solid #FF8000", borderRadius: "8px", color: "#FF8000", cursor: "pointer", fontWeight: "500", whiteSpace: "nowrap", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <PlusIcon />
            </button>
            <button type="button" onClick={() => setShowManageGroupsModal(true)} title="Manage Groups"
              style={{ width: "fit-content", padding: "12px", backgroundColor: "#fff", border: "1px solid #718096", borderRadius: "8px", color: "#718096", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <GearIcon />
            </button>
          </div>
        </div>

        {/* 2 — Role with Add + Manage Buttons */}
        <div style={{ marginBottom: "16px" }}>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <select name="role" value={formData.role} onChange={handleChange} required disabled={!formData.group}
              style={{ flex: 1, padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", backgroundColor: !formData.group ? "#f7fafc" : "white", cursor: !formData.group ? "not-allowed" : "pointer", color: !formData.group ? "#a0aec0" : "inherit" }}>
              <option value="">{formData.group ? "Select Role" : "Select a category first"}</option>
              {availableRoles.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <button type="button" title="Add Role"
              onClick={() => { if (!formData.group) { toast.error("Please select a group first before adding a role"); return; } setSelectedGroupForRole(formData.group); setShowRoleModal(true); }}
              style={{ width: "fit-content", padding: "12px", backgroundColor: "#fff", border: "1px solid #FF8000", borderRadius: "8px", color: "#FF8000", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <PlusIcon />
            </button>
            <button type="button" title="Manage Roles"
              onClick={() => { setManageRolesSelectedGroup(formData.group || ""); setShowManageRolesModal(true); }}
              style={{ width: "fit-content", padding: "12px", backgroundColor: "#fff", border: "1px solid #718096", borderRadius: "8px", color: "#718096", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <GearIcon />
            </button>
          </div>
        </div>

        {/* 3 — Designation with Add + Manage Buttons */}
        <div style={{ marginBottom: "16px" }}>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <select name="designation" value={formData.designation} onChange={handleChange}
              style={{ flex: 1, padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
              <option value="">Designation (optional)</option>
              {designations.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <button type="button" title="Add Designation" onClick={() => setShowDesignationModal(true)}
              style={{ width: "fit-content", padding: "12px", backgroundColor: "#fff", border: "1px solid #FF8000", borderRadius: "8px", color: "#FF8000", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <PlusIcon />
            </button>
            <button type="button" title="Manage Designations" onClick={() => setShowManageDesignationsModal(true)}
              style={{ width: "fit-content", padding: "12px", backgroundColor: "#fff", border: "1px solid #718096", borderRadius: "8px", color: "#718096", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <GearIcon />
            </button>
          </div>
        </div>

        {/* 4 — Full Name */}
        <input type="text" name="name" placeholder="Full Name (e.g. Irfan Mahmood)" value={formData.name} onChange={handleChange} required
          style={{ marginBottom: "16px", width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "16px" }} />

        {/* 5 — Image Upload with Drag & Drop */}
        <div style={{ marginBottom: "16px" }}>
          <label style={{ display: "block", marginBottom: "8px", fontWeight: "500", color: "#4a5568" }}>Profile Image</label>
          {imagePreview && (
            <div style={{ marginBottom: "12px" }}>
              <img src={imagePreview} alt="Preview" style={{ width: "80px", height: "80px", borderRadius: "50%", objectFit: "cover", border: "2px solid #e2e8f0" }} />
            </div>
          )}
          <label 
            ref={imageDropRef}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            style={{ 
              display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "8px", 
              width: "100%", minHeight: "100px", padding: "20px 16px", border: isDragOver ? "2px dashed #FF8000" : "1px solid #e2e8f0", 
              borderRadius: "10px", fontSize: "14px", backgroundColor: isDragOver ? "#fff7ed" : "#f9fafb", 
              cursor: "pointer", color: isDragOver ? "#FF8000" : "#6b7280", transition: "all 0.2s ease", 
              boxSizing: "border-box", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" 
            }}
            onMouseEnter={e => { if (!isDragOver) { e.currentTarget.style.borderColor = "#FF8000"; e.currentTarget.style.backgroundColor = "#fff7ed"; e.currentTarget.style.color = "#FF8000"; } }}
            onMouseLeave={e => { if (!isDragOver) { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.backgroundColor = "#f9fafb"; e.currentTarget.style.color = "#6b7280"; } }}
          >
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} style={{ display: "none" }} />
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="16 16 12 12 8 16" /><line x1="12" y1="12" x2="12" y2="21" /><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
            </svg>
            <span style={{ fontWeight: "500", fontSize: "14px" }}>
              {isDragOver ? "Drop image here" : (selectedFile?.name || "Click or drag & drop image here")}
            </span>
            <span style={{ fontSize: "12px", opacity: 0.7 }}>PNG, JPG, WEBP supported (Max 5MB)</span>
          </label>
        </div>
        <br />

        {/* Submit Buttons */}
        <div style={{ display: "flex", gap: "12px", marginTop: "8px", flexWrap: "wrap" }}>
          <button type="submit" disabled={loading || uploading}
            style={{ backgroundColor: "#FF8000", color: "white", padding: "12px 24px", border: "none", borderRadius: "8px", cursor: loading || uploading ? "not-allowed" : "pointer", fontSize: "16px", fontWeight: "600", opacity: loading || uploading ? 0.7 : 1, width: "auto", display: "inline-block" }}>
            {loading || uploading ? "Saving..." : editingId ? "Update Contributor" : "Add Contributor"}
          </button>
          {editingId && (
            <button type="button"
              onClick={() => { setEditingId(null); setFormData(EMPTY_FORM); setImagePreview(""); setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
              style={{ backgroundColor: "transparent", color: "#718096", padding: "12px 24px", border: "1px solid #e2e8f0", borderRadius: "8px", cursor: "pointer", fontSize: "16px", width: "auto", display: "inline-block" }}
              onMouseOver={(e) => { e.target.style.backgroundColor = "#f7fafc"; }}
              onMouseOut={(e) => { e.target.style.backgroundColor = "transparent"; }}>
              Cancel Edit
            </button>
          )}
        </div>
      </form>

      {/* ── Contributors Table ── */}
      <div className="job-records">
        <h3>All Contributors</h3>
        {loading ? (
          <LoadingAnimation />
        ) : contributors.length === 0 ? (
          <p>No contributors added yet.</p>
        ) : (
          <div className="table-wrapper">
            <table className="job-table">
              <thead>
                <tr>
                  <th>Photo</th><th>Name</th><th>Group</th><th>Role</th><th>Designation</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {contributors.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div style={{ width: "40px", height: "40px", borderRadius: "50%", overflow: "hidden", border: "2px solid #e2e8f0", backgroundColor: "#f7fafc", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {c.image ? <img src={c.image} alt={c.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <i className="fas fa-user" style={{ color: "#cbd5e0", fontSize: "16px" }}></i>}
                      </div>
                    </td>
                    <td>{c.name}</td>
                    <td>
                      <span style={{ fontSize: "11px", backgroundColor: "#fff3e0", color: "#FF8000", padding: "3px 10px", borderRadius: "20px", fontWeight: "600", whiteSpace: "nowrap" }}>{c.group}</span>
                    </td>
                    <td>{c.role}</td>
                    <td>{c.designation || <span style={{ color: "#cbd5e0" }}>—</span>}</td>
                    <td className="action_buttons">
                      <button onClick={() => handleEdit(c)}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 512 512">
                          <path fill="currentColor" d="M441 58.9L453.1 71c9.4 9.4 9.4 24.6 0 33.9L424 134.1L377.9 88L407 58.9c9.4-9.4 24.6-9.4 33.9 0zM209.8 256.2L344 121.9l46.1 46.1l-134.3 134.2c-2.9 2.9-6.5 5-10.4 6.1L186.9 325l16.7-58.5c1.1-3.9 3.2-7.5 6.1-10.4zM373.1 25L175.8 222.2c-8.7 8.7-15 19.4-18.3 31.1l-28.6 100c-2.4 8.4-.1 17.4 6.1 23.6s15.2 8.5 23.6 6.1l100-28.6c11.8-3.4 22.5-9.7 31.1-18.3L487 138.9c28.1-28.1 28.1-73.7 0-101.8L474.9 25c-28.1-28.1-73.7-28.1-101.8 0M88 64c-48.6 0-88 39.4-88 88v272c0 48.6 39.4 88 88 88h272c48.6 0 88-39.4 88-88V312c0-13.3-10.7-24-24-24s-24 10.7-24 24v112c0 22.1-17.9 40-40 40H88c-22.1 0-40-17.9-40-40V152c0-22.1 17.9-40 40-40h112c13.3 0 24-10.7 24-24s-10.7-24-24-24z" />
                        </svg>
                      </button>
                      <button onClick={() => handleDelete(c.id)}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                          <path fill="currentColor" d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6zm2.46-7.12l1.41-1.41L12 12.59l2.12-2.12l1.41 1.41L13.41 14l2.12 2.12l-1.41 1.41L12 15.41l-2.12 2.12l-1.41-1.41L10.59 14zM15.5 4l-1-1h-5l-1 1H5v2h14V4z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Managecontributors;